/**
 * The shift report — QA report §4.2.
 *
 * "اطبع كل إيصالات الوردية، أو إيصالاتي أنا فقط." A cashier handing over a
 * till at the end of a shift wants one sheet: every receipt they issued, what
 * was taken and by which method, what is still owed on deferred receipts,
 * what was voided — and two lines for the one handing over and the one
 * taking it. An accountant wants the same sheet for the whole shift, split by
 * employee.
 *
 * So the screen is three choices and a button:
 *
 *   اليوم      the day the shift starts (a night shift that runs past
 *              midnight still belongs to the evening it started on)
 *   الوردية    morning / evening / night / the whole day, or two times
 *   النطاق     my receipts, everybody's, or one employee's
 *
 * and the paper it prints. The window arithmetic and the sums live in
 * domain/shiftReport.js, where they are tested; this file fetches and draws.
 *
 * One fetch per window: the receipts are then filtered by issuer on the page,
 * so switching between "mine" and "all" is instant and the per-employee
 * chips can show their counts before they are pressed.
 */

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, EmptyState, ErrorState, Icon, Modal, Spinner, DateInput, TimeInput } from '../../components/common';
import { StatTile3D } from '../../components/common/Tile3D';
import { receiptsApi } from '../../api/endpoints';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionContext';
import { todayKey } from '../../domain/dates';
import { formatDmy, ltr } from '../../domain/bidi';
import { METHOD_LABELS, SHIFT_PRESETS, chronological, currentShift, shiftWindow, summariseShift } from '../../domain/shiftReport';
import { ReceiptPreview } from './ReceiptSheet';
import { formatMoney, printArea } from './labels';
import { pickLogo } from '../../domain/logoBackdrop';

const FETCH_LIMIT = 2000;

const PRESET_ICONS = { morning: 'sun', evening: 'sunset', night: 'moon', day: 'calendar-days' };

const STATUS = {
  issued: { label: 'آجل', className: 'border-amber-500/30 bg-amber-500/10 text-amber-200' },
  collected: { label: 'محصّل', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' },
  void: { label: 'ملغي', className: 'border-slate-500/30 bg-slate-500/10 text-slate-400' },
};

const METHOD_ICONS = { cash: 'banknote', card: 'credit-card', wallet: 'smartphone', transfer: 'arrow-right-left' };

const pad = (n) => String(n).padStart(2, '0');
const clock = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const stamp = (d) => `${formatDmy(d)} ${clock(d)}`;

/** What a receipt row prints as money — the same rules as the sums. */
function amountsOf(r) {
  if (r.status === 'void') return { total: 0, paid: 0, remaining: 0 };
  const total = Number(r.total || 0);
  const paid = Number(r.paidAmount ?? (r.status === 'collected' ? total : 0));
  return { total, paid, remaining: Math.max(0, Math.round((total - paid) * 100) / 100) };
}

const servicesOf = (r) =>
  (r.items || [])
    .map((item) => item.label)
    .filter(Boolean)
    .join('، ') || '—';

/* ========================================================================== */
/* The paper                                                                  */
/* ========================================================================== */

/**
 * One A4 sheet, or as many as the receipts need: the header, the sums, the
 * receipts in the order they were issued, and the handover signatures.
 */
const ShiftSheet = forwardRef(function ShiftSheet({ receipts, summary, period: win, shiftLabel, scopeLabel, printedBy, handedOverBy }, ref) {
  const { institutionName, institutionLogo, institutionLogoBackdrop } = usePermissions();
  // Paper is a light page: the light mark (المرحلة 1.35), never the dark one.
  const sheetLogo = pickLogo({ logo: institutionLogo, backdrop: institutionLogoBackdrop }, { dark: false });
  const printedAt = new Date();
  const methods = Object.entries(summary.byMethod);

  return (
    <div ref={ref} className="print-sheet shift-sheet" dir="rtl">
      <div className="shift-sheet__head">
        <div className="flex min-w-0 items-center gap-3">
          {sheetLogo.src && <img src={sheetLogo.src} alt="" className={`receipt-sheet__logo ${sheetLogo.plate}`} />}
          <div className="min-w-0">
            <h2 className="text-base font-bold">{institutionName || 'المنشأة الطبية'}</h2>
            <h1 className="text-xl font-bold">تقرير الوردية — {shiftLabel}</h1>
          </div>
        </div>
        <div className="text-left text-xs leading-6">
          <p>
            <b>من:</b> {ltr(stamp(win.from))}
          </p>
          <p>
            <b>إلى:</b> {ltr(stamp(win.to))}
          </p>
        </div>
      </div>

      <div className="shift-sheet__meta">
        <span>
          <b>النطاق:</b> {scopeLabel}
        </span>
        <span>
          <b>طُبع بواسطة:</b> {printedBy || '—'}
        </span>
        <span>
          <b>وقت الطباعة:</b> {ltr(stamp(printedAt))}
        </span>
      </div>

      {/* The sums first: they are what the handover is about. */}
      <div className="shift-sheet__sums">
        <div>
          <span>عدد الإيصالات</span>
          <b>{summary.count}</b>
          <small>
            نقدي {summary.cashCount} · آجل {summary.deferredCount} · ملغي {summary.voided}
          </small>
        </div>
        <div>
          <span>الإجمالي</span>
          <b>{formatMoney(summary.total)}</b>
        </div>
        <div className="shift-sheet__paid">
          <span>المُحصَّل فعلياً</span>
          <b>{formatMoney(summary.paid)}</b>
        </div>
        <div className={summary.remaining > 0 ? 'shift-sheet__due' : ''}>
          <span>المتبقي (آجل)</span>
          <b>{formatMoney(summary.remaining)}</b>
        </div>
      </div>

      {methods.length > 0 && (
        <p className="mt-2 text-xs">
          <b>المحصّل حسب طريقة الدفع:</b>{' '}
          {methods.map(([method, amount], i) => (
            <span key={method}>
              {i > 0 && ' · '}
              {METHOD_LABELS[method] || method}: {formatMoney(amount)}
            </span>
          ))}
        </p>
      )}

      {summary.byIssuer.length > 1 && (
        <table className="mt-3 shift-sheet__issuers">
          <thead>
            <tr>
              <th>الموظف</th>
              <th style={{ width: '14%' }}>الإيصالات</th>
              <th style={{ width: '22%' }}>الإجمالي</th>
              <th style={{ width: '22%' }}>المُحصَّل</th>
            </tr>
          </thead>
          <tbody>
            {summary.byIssuer.map((issuer) => (
              <tr key={String(issuer.id)}>
                <td>{issuer.name}</td>
                <td>{issuer.count}</td>
                <td>{formatMoney(issuer.total)}</td>
                <td>{formatMoney(issuer.paid)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <table className="mt-3 shift-sheet__table">
        <thead>
          <tr>
            <th style={{ width: '4%' }}>#</th>
            <th style={{ width: '13%' }}>رقم الإيصال</th>
            <th style={{ width: '6%' }}>الوقت</th>
            <th>المريض</th>
            <th>الخدمات</th>
            <th style={{ width: '5%' }}>النوع</th>
            <th style={{ width: '9%' }}>الإجمالي</th>
            <th style={{ width: '9%' }}>المدفوع</th>
            <th style={{ width: '9%' }}>المتبقي</th>
            <th style={{ width: '6%' }}>الحالة</th>
            <th style={{ width: '11%' }}>المُصدِر</th>
          </tr>
        </thead>
        <tbody>
          {receipts.map((r, i) => {
            const money = amountsOf(r);
            const issued = new Date(r.issuedAt);
            return (
              <tr key={r.id} className={r.status === 'void' ? 'shift-sheet__void' : ''}>
                <td>{i + 1}</td>
                <td dir="ltr" className="text-right font-mono">
                  {r.number}
                </td>
                <td>{ltr(clock(issued))}</td>
                <td>{r.patient?.fullName || '—'}</td>
                <td>{servicesOf(r)}</td>
                <td>{r.receiptType === 'deferred' ? 'آجل' : 'نقدي'}</td>
                <td>{r.status === 'void' ? '—' : formatMoney(money.total)}</td>
                <td>{r.status === 'void' ? '—' : formatMoney(money.paid)}</td>
                <td>{r.status === 'void' ? '—' : formatMoney(money.remaining)}</td>
                <td>{(STATUS[r.status] || STATUS.issued).label}</td>
                <td>{r.signatureName || '—'}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={6}>الإجمالي ({summary.count - summary.voided} إيصالاً ساريًا)</th>
            <th>{formatMoney(summary.total)}</th>
            <th>{formatMoney(summary.paid)}</th>
            <th>{formatMoney(summary.remaining)}</th>
            <th colSpan={2} />
          </tr>
        </tfoot>
      </table>

      {/* The handover: who gave the till, who took it, and the stamp. */}
      <div className="shift-sheet__signatures">
        <div>
          <p>المسلِّم</p>
          <p className="shift-sheet__signer">{handedOverBy || ' '}</p>
          <div className="receipt-signature__line" />
          <small>الاسم والتوقيع</small>
        </div>
        <div>
          <p>المستلِم</p>
          <p className="shift-sheet__signer"> </p>
          <div className="receipt-signature__line" />
          <small>الاسم والتوقيع</small>
        </div>
        <div>
          <p>ختم الخزينة</p>
          <p className="shift-sheet__signer"> </p>
          <div className="receipt-signature__line" />
          <small>التاريخ {ltr(formatDmy(printedAt))}</small>
        </div>
      </div>
    </div>
  );
});

/* ========================================================================== */
/* The screen                                                                 */
/* ========================================================================== */

/**
 * @param {'me'|'all'} defaultScope  reception opens on the clerk's own till,
 *   accounts on the whole shift.
 */
export function ShiftReport({ defaultScope = 'me' }) {
  const { user } = useAuth();
  const myId = user?.id ?? null;

  const [date, setDate] = useState(() => todayKey());
  const [presetId, setPresetId] = useState(() => currentShift(new Date()).id);
  const [custom, setCustom] = useState({ from: '08:00', to: '14:00' });
  const [scope, setScope] = useState(defaultScope);
  const [receipts, setReceipts] = useState(null);
  const [error, setError] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [viewing, setViewing] = useState(null);
  const sheet = useRef(null);

  const preset = SHIFT_PRESETS.find((p) => p.id === presetId);
  const times = preset ? { from: preset.from, to: preset.to } : custom;
  const win = useMemo(() => shiftWindow(date, times.from, times.to), [date, times.from, times.to]);
  const fromIso = win?.from.toISOString();
  const toIso = win?.to.toISOString();

  const load = useCallback(async () => {
    if (!fromIso || !toIso) return;
    setReceipts(null);
    setError(null);
    try {
      setReceipts(await receiptsApi.list({ from: fromIso, to: toIso, limit: FETCH_LIMIT }));
    } catch (err) {
      setError(err);
    }
  }, [fromIso, toIso]);

  useEffect(() => {
    load();
  }, [load]);

  const everyone = useMemo(() => summariseShift(receipts || []).byIssuer, [receipts]);
  const shown = useMemo(() => {
    const list = receipts || [];
    if (scope === 'all') return chronological(list);
    const target = scope === 'me' ? String(myId) : scope;
    return chronological(list.filter((r) => String(r.issuedBy) === target));
  }, [receipts, scope, myId]);
  const summary = useMemo(() => summariseShift(shown), [shown]);

  const mine = (receipts || []).filter((r) => String(r.issuedBy) === String(myId)).length;
  const others = everyone.filter((issuer) => String(issuer.id) !== String(myId));
  const chosenIssuer = everyone.find((issuer) => String(issuer.id) === scope);

  const shiftLabel = preset
    ? preset.label
    : `وردية مخصصة ${ltr(`${custom.from} – ${custom.to}`)}`;
  const scopeLabel =
    scope === 'all' ? 'كل إيصالات الوردية' : scope === 'me' ? `إيصالاتي — ${user?.name || ''}` : `إيصالات ${chosenIssuer?.name || 'موظف'}`;
  const handedOverBy = scope === 'me' ? user?.name : chosenIssuer?.name;

  const openReceipt = async (r) => setViewing(await receiptsApi.get(r.id).catch(() => r));

  return (
    <div className="space-y-5">
      {/* The three choices. */}
      <section className="glass-panel shift-controls rounded-2xl p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="shift-controls__label">
            <Icon name="calendar-days" size={16} /> اليوم
          </span>
          <DateInput value={date} onChange={(e) => e.target.value && setDate(e.target.value)} clearable={false} className="w-auto" />

          <span className="shift-controls__label ms-2">
            <Icon name="clock" size={16} /> الوردية
          </span>
          <div className="flex flex-wrap gap-2" role="group" aria-label="اختر الوردية">
            {SHIFT_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`chip-toggle ${presetId === p.id ? 'chip-toggle--on' : ''}`}
                aria-pressed={presetId === p.id}
                onClick={() => setPresetId(p.id)}
              >
                <Icon name={PRESET_ICONS[p.id]} size={14} />
                {p.label}
                <span className="chip-toggle__count">{ltr(`${p.from}–${p.to}`)}</span>
              </button>
            ))}
            <button
              type="button"
              className={`chip-toggle ${presetId === 'custom' ? 'chip-toggle--on' : ''}`}
              aria-pressed={presetId === 'custom'}
              onClick={() => setPresetId('custom')}
            >
              <Icon name="sliders-horizontal" size={14} />
              وقت آخر
            </button>
          </div>

          {presetId === 'custom' && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">من</span>
              <TimeInput value={custom.from} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, from: e.target.value }))} className="w-40" />
              <span className="text-xs text-slate-400">إلى</span>
              <TimeInput value={custom.to} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, to: e.target.value }))} className="w-40" />
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-white/5 pt-3">
          <span className="shift-controls__label">
            <Icon name="users" size={16} /> النطاق
          </span>
          <div className="flex flex-wrap gap-2" role="group" aria-label="إيصالات من">
            <button
              type="button"
              className={`chip-toggle ${scope === 'me' ? 'chip-toggle--on' : ''}`}
              aria-pressed={scope === 'me'}
              onClick={() => setScope('me')}
            >
              <Icon name="user-check" size={14} />
              إيصالاتي فقط
              <span className="chip-toggle__count">{receipts ? mine : '…'}</span>
            </button>
            <button
              type="button"
              className={`chip-toggle ${scope === 'all' ? 'chip-toggle--on' : ''}`}
              aria-pressed={scope === 'all'}
              onClick={() => setScope('all')}
            >
              <Icon name="users" size={14} />
              كل إيصالات الوردية
              <span className="chip-toggle__count">{receipts ? receipts.length : '…'}</span>
            </button>
            {others.map((issuer) => (
              <button
                key={String(issuer.id)}
                type="button"
                className={`chip-toggle ${scope === String(issuer.id) ? 'chip-toggle--on' : ''}`}
                aria-pressed={scope === String(issuer.id)}
                onClick={() => setScope(String(issuer.id))}
              >
                <Icon name="user" size={14} />
                {issuer.name}
                <span className="chip-toggle__count">{issuer.count}</span>
              </button>
            ))}
          </div>

          <div className="ms-auto flex flex-wrap gap-2">
            <Button variant="subtle" icon="refresh-cw" onClick={load}>
              تحديث
            </Button>
            <Button icon="printer" onClick={() => setPreviewing(true)} disabled={!receipts || !win}>
              معاينة وطباعة التقرير
            </Button>
          </div>
        </div>

        {win && (
          <p className="mt-3 text-xs text-slate-400">
            الفترة: من {ltr(stamp(win.from))} إلى {ltr(stamp(win.to))}
            {win.to.getDate() !== win.from.getDate() && ' (تمتد إلى اليوم التالي)'}
          </p>
        )}
      </section>

      {error && <ErrorState error={error} onRetry={load} />}
      {!error && !receipts && <Spinner label="جارٍ تحميل إيصالات الوردية" variant="inline" />}

      {!error && receipts && (
        <>
          {receipts.length >= FETCH_LIMIT && (
            <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
              الفترة فيها أكثر من {FETCH_LIMIT} إيصال — اختر وردية أقصر ليظهر التقرير كاملاً.
            </p>
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <StatTile3D icon="receipt" label="عدد الإيصالات" value={summary.count} hint={`نقدي ${summary.cashCount} · آجل ${summary.deferredCount}`} tone="cyan" delay={0} />
            <StatTile3D icon="banknote" label="الإجمالي" value={formatMoney(summary.total)} tone="purple" delay={1} />
            <StatTile3D icon="coins" label="المُحصَّل فعلياً" value={formatMoney(summary.paid)} tone="emerald" delay={2} />
            <StatTile3D icon="hourglass" label="المتبقي (آجل)" value={formatMoney(summary.remaining)} tone="amber" delay={3} />
            <StatTile3D icon="ban" label="إيصالات ملغاة" value={summary.voided} tone="slate" delay={4} />
          </div>

          {Object.keys(summary.byMethod).length > 0 && (
            <div className="flex flex-wrap gap-3">
              {Object.entries(summary.byMethod).map(([method, amount]) => (
                <div key={method} className="method-pill">
                  <Icon name={METHOD_ICONS[method] || 'wallet'} size={18} />
                  <span>{METHOD_LABELS[method] || method}</span>
                  <b>{formatMoney(amount)}</b>
                </div>
              ))}
            </div>
          )}

          {scope === 'all' && everyone.length > 1 && (
            <section className="glass-panel rounded-2xl p-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
                <Icon name="users" size={16} className="text-cyan-300" /> حسب الموظف
              </h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {everyone.map((issuer) => (
                  <button
                    key={String(issuer.id)}
                    type="button"
                    className="issuer-card"
                    onClick={() => setScope(String(issuer.id) === String(myId) ? 'me' : String(issuer.id))}
                  >
                    <span className="issuer-card__avatar">{issuer.name.charAt(0)}</span>
                    <span className="min-w-0 flex-1 text-right">
                      <span className="block truncate font-bold text-white">{issuer.name}</span>
                      <span className="block text-xs text-slate-400">{issuer.count} إيصال</span>
                    </span>
                    <b className="text-emerald-300">{formatMoney(issuer.paid)}</b>
                  </button>
                ))}
              </div>
            </section>
          )}

          {shown.length === 0 ? (
            <EmptyState
              icon="receipt"
              title="لا توجد إيصالات في هذه الوردية"
              message={scope === 'me' ? 'لم تُصدر إيصالاً في هذه الفترة. جرّب «كل إيصالات الوردية» أو وردية أخرى.' : 'جرّب يوماً أو وردية أخرى.'}
            />
          ) : (
            <section className="glass-panel rounded-2xl p-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
                <Icon name="list" size={16} className="text-cyan-300" /> الإيصالات بترتيب إصدارها ({shown.length})
              </h3>
              <div className="custom-scrollbar -mx-2 overflow-x-auto px-2">
                <table className="shift-table w-full min-w-[820px] text-right text-sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>رقم الإيصال</th>
                      <th>الوقت</th>
                      <th>المريض</th>
                      <th>الخدمات</th>
                      <th>الإجمالي</th>
                      <th>المدفوع</th>
                      <th>المتبقي</th>
                      <th>الحالة</th>
                      <th>المُصدِر</th>
                      <th aria-label="طباعة" />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r, i) => {
                      const money = amountsOf(r);
                      const status = STATUS[r.status] || STATUS.issued;
                      return (
                        <tr key={r.id} className={r.status === 'void' ? 'opacity-50' : ''}>
                          <td className="text-slate-500">{i + 1}</td>
                          <td className="font-mono text-cyan-200" dir="ltr">
                            {r.number}
                          </td>
                          <td className="text-slate-300">{ltr(clock(new Date(r.issuedAt)))}</td>
                          <td className="font-bold text-white">
                            <span className="block max-w-[10rem] truncate" title={r.patient?.fullName}>
                              {r.patient?.fullName || '—'}
                            </span>
                          </td>
                          <td className="text-slate-300">
                            <span className="block max-w-[10rem] truncate" title={servicesOf(r)}>
                              {servicesOf(r)}
                            </span>
                          </td>
                          <td className="text-white">{r.status === 'void' ? '—' : formatMoney(money.total)}</td>
                          <td className="text-emerald-300">{r.status === 'void' ? '—' : formatMoney(money.paid)}</td>
                          <td className={money.remaining > 0 ? 'text-amber-300' : 'text-slate-500'}>
                            {r.status === 'void' ? '—' : formatMoney(money.remaining)}
                          </td>
                          <td>
                            <Badge className={status.className}>{status.label}</Badge>
                          </td>
                          <td className="text-slate-300">
                            <span className="block max-w-[7rem] truncate" title={r.signatureName}>
                              {r.signatureName || '—'}
                            </span>
                          </td>
                          <td>
                            <Button variant="subtle" icon="printer" onClick={() => openReceipt(r)} aria-label={`طباعة ${r.number}`} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      <Modal
        open={previewing}
        onClose={() => setPreviewing(false)}
        title="معاينة تقرير الوردية"
        icon="printer"
        size="xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="subtle" onClick={() => setPreviewing(false)}>
              إغلاق
            </Button>
            <Button icon="printer" onClick={() => printArea(sheet.current, { title: 'تقرير الوردية' })}>
              طباعة
            </Button>
          </div>
        }
      >
        {previewing && win && (
          <ShiftSheet
            ref={sheet}
            receipts={shown}
            summary={summary}
            period={win}
            shiftLabel={shiftLabel}
            scopeLabel={scopeLabel}
            printedBy={user?.name}
            handedOverBy={handedOverBy}
          />
        )}
      </Modal>

      <Modal open={Boolean(viewing)} onClose={() => setViewing(null)} title={viewing?.number} icon="receipt" size="lg">
        {viewing && <ReceiptPreview receipt={viewing} />}
      </Modal>
    </div>
  );
}

export default ShiftReport;
